/**
 * The site's route list, shared by the SPA's router (`App.tsx`) and the SEO
 * server's route table (`server/routes.mjs`, web plan W1.3), so the server
 * answers 200 for exactly the URLs the SPA renders and 404 for the rest.
 *
 * Pure data (no React, no `window`): `App.tsx` maps each `page` key to its
 * lazy component; the server maps it to a status and a head.
 *
 * - `LEGACY_ROUTES`: the un-prefixed ("legacy") URLs: home, the catalog lists,
 *   the id/slug entity pages, and the global pages (auth, account, legal, token
 *   links, and the company pages once their owner copy exists) that stay
 *   un-prefixed for good (master D-2).
 * - `localeRoutes(locale)`: the routes under a launched `/{cc}/{lang}` prefix,
 *   with LITERAL segments (never a dynamic `/:cc/:lang`, which would swallow
 *   two-segment global URLs such as `/mobile/privacy-policy`). With the URL
 *   move live (`LOCALE_ROUTES_LIVE`, web plan W2) they include the catalog
 *   pages, generated from the `catalog` routes below, whose un-prefixed paths
 *   then only redirect (`movedPath`).
 *
 * A static page's `seo` is the head it renders itself (`<PageHead title
 * description noindex />`), which the server sends before JavaScript runs;
 * src/App.serverHeads.test.tsx renders every one of them in the SPA and
 * compares.
 */
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@/seo/locales';
import { liveTrustPages, type TrustPageKey } from '@/seo/trustPages';
import { chooserLive, LOCALE_ROUTES_LIVE, localePath, type EntityType } from '@/seo/urls';

/** Which page component a route renders (App.tsx) and which head it gets (server). */
export type RoutePage =
  | 'home'
  | 'brands'
  | 'shops'
  | 'promotions'
  | 'search'
  | 'nearby'
  | 'brand'
  | 'shop'
  | 'store'
  | 'promotion'
  | 'favorites'
  | 'notifications'
  | 'notificationSettings'
  | 'profile'
  | 'settings'
  | 'feedback'
  | 'privacy'
  | 'mobilePrivacy'
  | 'deleteAccount'
  | 'terms'
  | 'login'
  | 'register'
  | 'verifyOtp'
  | 'confirmEmail'
  | 'forgotPassword'
  | 'resetPassword'
  | 'localeHome'
  | 'countryHome'
  | 'saleCalendar'
  | 'saleEvent'
  | 'category'
  | 'city'
  | 'brand-city'
  | TrustPageKey;

/** The head a page without data renders: `<PageHead title description noindex />`. */
export interface StaticRouteSeo {
  title: string;
  /** Omitted: the site description. */
  description?: string;
  noindex: boolean;
}

export interface AppRoute {
  /** React Router path pattern (`:param` segments). */
  path: string;
  page: RoutePage;
  /** The entity a detail route shows (`:ref` is an id or a slug). */
  entity?: EntityType;
  /** A catalog list read with `?page=N` (web plan W0.11): its canonical keeps `?page=N` (N ≥ 2). */
  paginated?: boolean;
  /**
   * A link people get by email (confirm, password reset): its token is case
   * sensitive, so the path is never lowercased or otherwise rewritten.
   */
  token?: boolean;
  /** For pages whose head does not depend on data (see the module comment). */
  seo?: StaticRouteSeo;
  /**
   * A catalog page (home, the lists, nearby, search): it moves under the
   * launched locale prefix with the URL move (`LOCALE_ROUTES_LIVE`, web plan
   * W2). Its un-prefixed path then only redirects there (`movedPath`).
   */
  catalog?: boolean;
}

/** `/search` stays noindex but has its own title and description (also the SPA's, via SearchPage). */
export const SEARCH_PAGE_TITLE = 'Search Brands, Shops & Promotions';
export const SEARCH_PAGE_DESCRIPTION =
  'Search PromoStork for brands, shops and promotions: find current sales, offers and deals by name or category.';

const noindexPage = (title: string, description?: string): StaticRouteSeo =>
  description === undefined ? { title, noindex: true } : { title, description, noindex: true };

/** The un-prefixed routes, in App.tsx's order. The catch-all (`*` → NotFoundPage) is not listed. */
export const LEGACY_ROUTES: readonly AppRoute[] = Object.freeze([
  { path: '/', page: 'home', catalog: true },

  // Catalog
  { path: '/brands', page: 'brands', paginated: true, catalog: true },
  { path: '/shops', page: 'shops', paginated: true, catalog: true },
  { path: '/promotions', page: 'promotions', paginated: true, catalog: true },
  {
    path: '/search',
    page: 'search',
    seo: noindexPage(SEARCH_PAGE_TITLE, SEARCH_PAGE_DESCRIPTION),
    catalog: true,
  },

  // Entity details (web plan W0.16): an ObjectId or a slug.
  { path: '/brand/:ref', page: 'brand', entity: 'brand' },
  { path: '/shop/:ref', page: 'shop', entity: 'shop' },
  { path: '/store/:ref', page: 'store', entity: 'store' },
  { path: '/promotion/:ref', page: 'promotion', entity: 'promotion' },

  // Account (gated in-page: login CTA when guest)
  { path: '/favorites', page: 'favorites', seo: noindexPage('Favorites') },
  { path: '/notifications', page: 'notifications', seo: noindexPage('Notifications') },
  {
    path: '/notification-settings',
    page: 'notificationSettings',
    seo: noindexPage('Notification settings'),
  },
  { path: '/profile', page: 'profile', seo: noindexPage('Profile') },
  { path: '/settings', page: 'settings', seo: noindexPage('Settings') },

  // Nearby map
  { path: '/nearby', page: 'nearby', catalog: true },

  // Feedback (open to guests & logged-in users)
  { path: '/feedback', page: 'feedback', seo: noindexPage('Send feedback') },

  // Legal. `/privacy` covers the website; `/mobile/privacy-policy` covers the
  // Android/iOS app and is the URL submitted to the Play Console.
  // `/delete-account` is the Play Console "Delete account URL": it must stay
  // publicly reachable (200) and never be gated behind a login.
  { path: '/privacy', page: 'privacy' },
  {
    path: '/mobile/privacy-policy',
    page: 'mobilePrivacy',
    seo: noindexPage(
      'Privacy Policy — PromoStork Mobile App',
      'How the PromoStork mobile app collects, uses and shares your information.',
    ),
  },
  {
    path: '/delete-account',
    page: 'deleteAccount',
    seo: noindexPage(
      'Delete your PromoStork account',
      'How to permanently delete your PromoStork account and what happens to your data.',
    ),
  },
  { path: '/terms', page: 'terms' },

  // Company (web plan W3.7): About, How we verify deals, For businesses,
  // Contact — global pages (master D-2), listed only while live (their owner
  // copy is written, @/seo/trustPages). A trust page that is not live is no
  // route: the SPA renders NotFoundPage and the server answers 404.
  ...liveTrustPages().map((page): AppRoute => ({ path: page.path, page: page.key })),

  // Auth
  { path: '/login', page: 'login', seo: noindexPage('Login') },
  { path: '/register', page: 'register', seo: noindexPage('Create account') },
  { path: '/verify-otp', page: 'verifyOtp', seo: noindexPage('Verify email') },
  { path: '/user/confirm/:token', page: 'confirmEmail', token: true, seo: noindexPage('Confirm email') },
  { path: '/forgot-password', page: 'forgotPassword', seo: noindexPage('Forgot password') },
  // CANONICAL. This must byte-match what the backend emails —
  // `${base}/user/reset-password/${token}` in
  // ../../promostork_backend/src/utils/forgetPasswordUrl.ts:29. The second
  // path is the short alias the Dashboard also registers, for hand-typed and
  // older links; the token-less one renders the invalid-link card instead of
  // NotFoundPage. A signed-in user following a reset link must NOT be bounced.
  {
    path: '/user/reset-password/:token',
    page: 'resetPassword',
    token: true,
    seo: noindexPage('Reset password'),
  },
  { path: '/reset-password/:token', page: 'resetPassword', token: true, seo: noindexPage('Reset password') },
  { path: '/user/reset-password', page: 'resetPassword', token: true, seo: noindexPage('Reset password') },
] satisfies AppRoute[]);

/**
 * The routes under one launched locale's literal prefix (`/pk/en`):
 *  - before the URL move (web plan W2, `live` false): the prefix itself → `/`
 *    (the SPA with `<Navigate replace>`, the server with a 302), and no
 *    catalog page under the prefix. Only for DEFAULT_LOCALE: another
 *    country's catalog has no un-prefixed copy, so its pages are always the
 *    moved ones below (as `catalogPath` links them, switch or not);
 *  - after it (`live`): the catalog pages (`catalog: true` above: home at the
 *    prefix itself, `/pk/en/brands`, `/pk/en/shops`, `/pk/en/promotions`,
 *    `/pk/en/search`, `/pk/en/nearby`), and `/{cc}` → its first launched
 *    locale (`countryHome`: the SPA replaces, the server 301s);
 *  - the sale calendar and event hubs (W0.15), born at their final URLs; an
 *    unknown event is a 404;
 *  - the category hubs (W3.1), born at their final URLs too (the backend's
 *    `Category.urlPath` is always `/{cc}/{lang}/category/…`): levels 1 and 2
 *    only, so a third segment matches no route (404); which slugs exist is
 *    the backend's `categoryPage` answer;
 *  - the entity pages' final URLs (the backend's `urlPath` once
 *    `PUBLIC_SLUG_TYPES` lists the type).
 */
export function localeRoutes(locale: Locale): AppRoute[] {
  return localeRoutesFor(locale, LOCALE_ROUTES_LIVE);
}

/** `localeRoutes` with the URL-move switch given (tests: both states in one run). */
export function localeRoutesFor(locale: Locale, switchOn: boolean): AppRoute[] {
  const prefix = localePath(locale);
  const live = switchOn || locale.cc !== DEFAULT_LOCALE.cc;
  const catalog: AppRoute[] = live
    ? LEGACY_ROUTES.filter((route) => route.catalog).map(({ catalog: _moved, ...route }) => ({
        ...route,
        path: localePath(locale, route.path),
      }))
    : [];
  const firstOfCountry = LOCALES.find((launched) => launched.cc === locale.cc) === locale;
  const country: AppRoute[] = live && firstOfCountry ? [{ path: `/${locale.cc}`, page: 'countryHome' }] : [];
  return [
    ...(live ? [] : [{ path: prefix, page: 'localeHome' } satisfies AppRoute]),
    ...catalog,
    ...country,
    { path: `${prefix}/sales`, page: 'saleCalendar' },
    { path: `${prefix}/sale/:event`, page: 'saleEvent' },
    { path: `${prefix}/category/:l1`, page: 'category' },
    { path: `${prefix}/category/:l1/:l2`, page: 'category' },
    { path: `${prefix}/city/:city`, page: 'city' },
    // The brand × city hub (W3.3): must appear before the brand entity route
    // so the 5-segment path /{cc}/{lang}/brand/:brand/:city is matched first.
    { path: `${prefix}/brand/:brand/:city`, page: 'brand-city' },
    { path: `${prefix}/brand/:ref`, page: 'brand', entity: 'brand' },
    { path: `${prefix}/shop/:ref`, page: 'shop', entity: 'shop' },
    { path: `${prefix}/store/:ref`, page: 'store', entity: 'store' },
    { path: `${prefix}/promotion/:ref`, page: 'promotion', entity: 'promotion' },
  ];
}

/**
 * Where an un-prefixed catalog route lives after the URL move
 * (`/brands` → `/pk/en/brands`, `/` → `/pk/en`), or null: before the move,
 * and for every other route. Un-prefixed pages belong to DEFAULT_LOCALE
 * (master D-11: the URL decides the locale, never the browser or the IP).
 * The SPA replaces the URL with it, the server redirects there in one hop.
 */
export function movedPath(route: AppRoute): string | null {
  return movedPathFor(route, LOCALE_ROUTES_LIVE);
}

/** `movedPath` with the URL-move switch given (tests: both states in one run). */
export function movedPathFor(route: AppRoute, live: boolean, chooser: boolean = chooserLive()): string | null {
  // With the country chooser on (web plan MW10) `/` stays: it is the chooser page.
  if (live && chooser && route.page === 'home') return null;
  return live && route.catalog ? localePath(DEFAULT_LOCALE, route.path) : null;
}
